// İSG uzmanı portalı — Kontrol aşaması **salt-okunur** panelleri (api-contract §31.5). Departman-
// kapsamlı detay (`DepartmentReviewDetailDto`) içeriğini gösterir: sorumlu belgeler + personel + AI
// notları (Aksiyonlar) + eğitimler/araçlar (yalnız İSG). İçerik **tek §31 ucundan** gelir (§14
// panelleri KULLANILMAZ — onlar kendi kendine `permits/get`/`permits/update` çağırır; Hekim/İK bu
// grant'ları taşımaz → 403). Bu yüzden burada yazma/aksiyon yok; onay/ret ayrı aksiyon çubuğundadır.
//
// **`ReviewActivitiesPanel` gövdesi `ActivityTimelineItem.tsx`'te PAYLAŞILIR** (owner review
// B3-3, §35.1) — izin detayının "Aksiyonlar" tabıyla (`PermitActivityPanel.tsx`) AYNI DTO'yu (§35.1
// "aynı listeyi taşır") ayrı ayrı yorumlamak, S35'in yeni alanlarını (event/actorType/durum
// geçişi/departman/gerekçe/hedef) iki yerde AYRIŞTIRMA riski taşırdı; artık TEK kaynak.

import {
  Card,
  CardContent,
  type DocumentTypeRef,
  EmptyState,
  PersonnelMaskNotice,
  REQUIREMENT_ENTITIES,
  type RequiredTrainingDto,
  Tag,
  type WorkPermitActivityDto,
  type WorkPermitPersonnelDto,
  type WorkPermitVehicleDto,
  documentTypeRefKey,
  useDocumentTypes,
} from '@isgportal/shared';
import { GraduationCap, type LucideIcon, Truck, Users } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { newestFirst } from '../../lib/activityOrder';
import { ActivityAvatar, ActivityBody } from '../permits/ActivityTimelineItem';
import { departmentApprovalTone } from './departmentEnums';

/** Sorumlu belge grubu ikonları — aşağıdaki personel/araç panelleriyle aynı. */
const GROUP_ICONS: Record<DocumentTypeRef['entity'], LucideIcon> = {
  personnel: Users,
  vehicle: Truck,
};

/**
 * Sorumlu belge türleri kartı — bu departmanın Kontrol'de incelediği belgeler (§31.5). Liste
 * `DocumentTypeRef[]`'tir ve entity'ye göre gruplanır: aynı kod iki entity'de olabilir (`Insurance`),
 * etiket her zaman `(entity, code)` ile çözülür.
 */
export function ResponsibleDocumentsCard({ documents }: { documents: DocumentTypeRef[] }) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const headingId = useId();
  return (
    <Card>
      <CardContent className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-fg">
          {t('departmentReview.detail.responsibleDocsTitle')}
        </h2>
        {documents.length === 0 ? (
          <EmptyState variant="panel" title={t('departmentReview.detail.responsibleDocsEmpty')} />
        ) : (
          <div className="flex flex-col gap-3">
            {REQUIREMENT_ENTITIES.map((entity) => {
              const refs = documents.filter((d) => d.entity === entity);
              if (refs.length === 0) {
                return null;
              }
              const Icon = GROUP_ICONS[entity];
              const groupHeadingId = `${headingId}-${entity}`;
              return (
                <div key={entity} className="flex flex-col gap-1.5">
                  <span
                    id={groupHeadingId}
                    className="flex items-center gap-1.5 text-xs font-medium text-muted"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {t(`departmentReview.detail.responsibleDocs.${entity}`)}
                  </span>
                  <ul aria-labelledby={groupHeadingId} className="flex flex-wrap gap-1.5">
                    {refs.map((ref) => (
                      <li key={documentTypeRefKey(ref)}>
                        <Tag color="gray" wrap>
                          {label(ref.entity, ref.code)}
                        </Tag>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Personel (salt-okunur): ad + iletişim + eğitim ilerleme + hazırlık + eksik belgeler. */
export function ReviewPersonnelPanel({ personnel }: { personnel: WorkPermitPersonnelDto[] }) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  if (personnel.length === 0) {
    return <EmptyState variant="panel" title={t('permits.personnel.empty')} />;
  }
  return (
    <div className="flex flex-col gap-3">
      {personnel.some((p) => p.personalDataMasked) ? <PersonnelMaskNotice /> : null}
      <ul className="flex flex-col gap-3">
        {personnel.map((p) => (
          <li key={p.uuid} className="flex flex-col gap-2 rounded-md border border-border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted" aria-hidden="true" />
                <span className="font-medium text-fg">
                  {p.firstName} {p.lastName}
                </span>
              </div>
              <Tag color={p.ready ? 'green' : 'red'}>
                {p.ready ? t('permits.personnel.ready') : t('permits.personnel.notReady')}
              </Tag>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted">
              <span>{p.emailOrPhone ?? '—'}</span>
              <span>
                {t('permits.personnel.col.training')}:{' '}
                {t('permits.personnel.trainingProgress', {
                  completed: p.completedTrainingCount,
                  required: p.requiredTrainingCount,
                })}
              </span>
            </div>
            {p.missingDocumentTypes.length > 0 ? (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted">
                  {t('permits.personnel.missingDocuments')}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {p.missingDocumentTypes.map((dt) => (
                    <Tag key={dt} color="red" wrap>
                      {label('personnel', dt)}
                    </Tag>
                  ))}
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Gerekli eğitimler (salt-okunur; yalnız İSG dolu gelir). */
export function ReviewTrainingsPanel({ trainings }: { trainings: RequiredTrainingDto[] }) {
  const { t } = useTranslation();
  if (trainings.length === 0) {
    return <EmptyState variant="panel" title={t('departmentReview.detail.trainingsEmpty')} />;
  }
  return (
    <ul className="flex flex-col gap-2">
      {trainings.map((tr) => (
        <li
          key={tr.trainingUuid}
          className="flex items-center gap-2 rounded-md border border-border p-3 text-sm text-fg"
        >
          <GraduationCap className="h-4 w-4 text-muted" aria-hidden="true" />
          {tr.title}
        </li>
      ))}
    </ul>
  );
}

/** Araçlar + belgeleri (salt-okunur; yalnız İSG dolu gelir). */
export function ReviewVehiclesPanel({ vehicles }: { vehicles: WorkPermitVehicleDto[] }) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  if (vehicles.length === 0) {
    return <EmptyState variant="panel" title={t('departmentReview.detail.vehiclesEmpty')} />;
  }
  return (
    <ul className="flex flex-col gap-3">
      {vehicles.map((v) => (
        <li key={v.uuid} className="flex flex-col gap-2 rounded-md border border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted" aria-hidden="true" />
              <span className="font-medium text-fg">{v.plate}</span>
            </div>
            <Tag color={v.vehicleReady ? 'green' : 'red'}>
              {v.vehicleReady ? t('permits.personnel.ready') : t('permits.personnel.notReady')}
            </Tag>
          </div>
          {v.documents.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {v.documents.map((doc) => (
                <li
                  key={doc.uuid}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="min-w-0 break-words text-fg">{label('vehicle', doc.type)}</span>
                  <Tag color={departmentApprovalTone(doc.approvalStatus)}>
                    {t(`departmentReview.status.${doc.approvalStatus}`)}
                  </Tag>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-sm text-muted">{t('departmentReview.detail.vehicleNoDocs')}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** AI notları + Aksiyonlar (salt-okunur zaman-çizelgesi; §31.3/§31.5). Gövde `ActivityTimelineItem.tsx`'te
 * PAYLAŞILIR (dosya başlığı notu) — bu panelin TEK kendi payı, dış kutulu kart çerçevesidir
 * (`rounded-md border`), `PermitActivityPanel.tsx`'in saç-çizgisi listesinden FARKLI, kasıtlı.
 * Liste en yeni üstte gösterilir: API'nin eskiden yeniye sırası `lib/activityOrder.ts` ile
 * istemcide BİLİNÇLİ olarak ters çevrilir; bu, `api-contract.md` §31.5/§35.1 ile bir uyumsuzluk DEĞİLDİR. */
export function ReviewActivitiesPanel({ activities }: { activities: WorkPermitActivityDto[] }) {
  const { t } = useTranslation();
  if (activities.length === 0) {
    return <EmptyState variant="panel" title={t('permits.activities.empty')} />;
  }
  return (
    <ol className="flex flex-col gap-3">
      {newestFirst(activities).map((activity) => (
        <li key={activity.uuid} className="flex gap-3">
          <ActivityAvatar kind={activity.kind} />
          <ActivityBody activity={activity} className="rounded-md border border-border p-3" />
        </li>
      ))}
    </ol>
  );
}
